import * as math from "mathjs";
import { SensorType } from "../../shared.js";
import { initializeUnits } from "../../utils/addMathUnits.js";
import { SensorConfigurations, UnitCategory } from "../SensorConfigurations/SensorConfiguration.js";
import { UnitCategoryConfigurations } from "./UnitCategory/UnitCategoryConfigurations.js";
import { Unit, UnitConfiguration, UnitConfigurations } from "./UnitConfigurations.js";
import { UnitPreferences } from "./UnitPresetsConfigurations.js";
/**
 * Converts a value to the preferred unit for a given unit category.
 * Pass currentUnit to convert from a specific unit; omit to use the category's database unit.
 */
export const convertToPreferredUnitByCategory = ({
    value,
    unitCategory,
    unitPreferences,
    round = true,
    currentUnit,
}: {
    value: number;
    unitCategory: UnitCategory;
    unitPreferences: UnitPreferences;
    round?: boolean;
    currentUnit?: Unit;
}) => {
    const databaseUnit = UnitCategoryConfigurations[unitCategory].databaseUnit;
    const targetUnit = unitPreferences[unitCategory];
    return convertToUnit({ value, currentUnit: currentUnit ?? databaseUnit, targetUnit, round });
};

/**
 * Converts a value to the preferred unit for a given sensor type.
 * Resolves the sensor type to its unit category, then delegates to convertToPreferredUnitByCategory.
 */
export const convertToPreferredUnitBySensorType = ({
    value,
    sensorType,
    unitPreferences,
    round = true,
    currentUnit,
}: {
    value: number;
    sensorType: SensorType;
    unitPreferences: UnitPreferences;
    round?: boolean;
    currentUnit?: Unit;
}) => {
    const unitCategory = SensorConfigurations[sensorType].unitCategory;
    return convertToPreferredUnitByCategory({ value, unitCategory, unitPreferences, round, currentUnit });
};

/**
 * Helper function to convert a value to another unit and optionally round the result
 */
const convertAndRound = (value: number, currentUnit: string, targetUnit: string, round = true) => {
    const valueUnit = math.unit(value, currentUnit);
    const convertedValue = math.number(valueUnit, targetUnit);

    if (!round) {
        return convertedValue;
    }

    return roundToDecimalPlaces(convertedValue);
};
/**
 * Convert a value(s) from one unit to another given their Units (UnitKeys)
 * Accepts both numbers and arrays of numbers.
 */
type ConvertResponseValue<T> = T extends number[] ? number[] : number;
export const convertToUnit = <T extends number | number[]>({
    value,
    currentUnit,
    targetUnit,
    round = true,
}: {
    value: T;
    currentUnit: Unit;
    targetUnit: Unit;
    round?: boolean;
}): {
    value: ConvertResponseValue<T>;
    unit: Unit;
    unitConfiguration: UnitConfiguration;
} => {
    let resultValue: number | number[];
    let resultUnit: Unit;
    let resultConfig: UnitConfiguration = UnitConfigurations[currentUnit];

    if (
        currentUnit !== targetUnit &&
        (!unitExists(UnitConfigurations[targetUnit].mathjs) || !unitExists(UnitConfigurations[currentUnit].mathjs))
    ) {
        console.warn(
            `Trying to convert ${value} ${currentUnit} to ${targetUnit} but ${targetUnit} or ${currentUnit} does not exist in the mathjs library`,
        );
        resultValue = value;
        resultUnit = currentUnit;
    } else {
        const currentMathjsUnit = UnitConfigurations[currentUnit].mathjs;
        const targetMathjsUnit = UnitConfigurations[targetUnit].mathjs;
        try {
            if (Array.isArray(value)) {
                resultValue = value.map((v) => convertAndRound(v, currentMathjsUnit, targetMathjsUnit, round));
                resultUnit = targetUnit;
                resultConfig = UnitConfigurations[targetUnit];
            } else {
                resultValue = convertAndRound(value, currentMathjsUnit, targetMathjsUnit, round);
                resultUnit = targetUnit;
                resultConfig = UnitConfigurations[targetUnit];
            }
        } catch (error) {
            console.error("error converting to unit", error);
            resultValue = value;
            resultUnit = currentUnit;
        }
    }

    return { value: resultValue as ConvertResponseValue<T>, unit: resultUnit, unitConfiguration: resultConfig };
};

/** A per-value converter for one unit pair, matching `convertToUnit` up to float error. */
export function createUnitConverter(
    fromUnit: Unit,
    toUnit: Unit,
    { round = true }: { round?: boolean } = {},
): (value: number) => number {
    const finish = round ? (value: number) => roundToDecimalPlaces(value) : (value: number) => value;
    if (fromUnit === toUnit) return finish;
    const { slope, offset } = deriveLinearConversionFactors(fromUnit, toUnit);
    return (value) => finish(value * slope + offset);
}

/**
 * Derives the `slope`/`offset` coefficients of the affine transform mapping `fromUnit` to `toUnit`,
 * by sampling mathjs's own unit-conversion knowledge at two points rather than hand-deriving and
 * hardcoding the constant in a sensor element config.
 *
 * Works uniformly for pure-ratio conversions (e.g. inch -> millimeter, where `offset` comes out 0)
 * and for affine ones (e.g. fahrenheit -> celsius): mathjs treats temperature as a point conversion
 * rather than a scale factor, so naively reading off `1 fahrenheit -> X celsius` would return the
 * wrong number for `slope`. Sampling at 0 and 1 and solving algebraically recovers the correct
 * `slope` and `offset` in both cases, with mathjs's own unit tables as the sole source of truth —
 * so a typo in a hand-typed conversion constant can't silently corrupt stored data.
 *
 * Intended to feed `SensorConversions.linearWithMinAndMax`'s `slope`/`offset` params.
 */
export function deriveLinearConversionFactors(fromUnit: Unit, toUnit: Unit): { slope: number; offset: number } {
    // Idempotent — guarantees custom units (e.g. inHg) are registered regardless of module import
    // order, since this may run at another module's top level before any app bootstrap does.
    initializeUnits();

    const fromMathjs = UnitConfigurations[fromUnit].mathjs;
    const toMathjs = UnitConfigurations[toUnit].mathjs;

    const offset = math.number(math.unit(0, fromMathjs), toMathjs);
    const onePlusOffset = math.number(math.unit(1, fromMathjs), toMathjs);

    return { slope: onePlusOffset - offset, offset };
}

/**
 * Convert a value from one unit to another
 * Legacy function that accepts mathjs units directly as strings rather
 * than the new concept of Units (UnitKeys).
 * @deprecated - use convertToUnit instead
 */
export const convertToUnitLegacy = ({
    value,
    currentUnit,
    targetUnit,
    round = true,
}: {
    value: number;
    currentUnit: string;
    targetUnit: string;
    round?: boolean;
}): { value: number; unit: string } => {
    // If either unit does not exist in the mathjs, return the value in the current unit
    if (!unitExists(targetUnit) || !unitExists(currentUnit)) {
        console.warn(
            `Trying to convert ${value} ${currentUnit} to ${targetUnit} but ${targetUnit} or ${currentUnit} does not exist in the mathjs library`,
        );
        return { value: value, unit: currentUnit };
    }

    // If the units are incompatable, return the value in the current unit
    if (!canConvertUnits(currentUnit, targetUnit)) {
        console.warn(
            `Trying to convert ${value} ${currentUnit} to ${targetUnit} but ${currentUnit} cannot be converted to ${targetUnit}`,
        );
        return { value: value, unit: currentUnit };
    }

    try {
        const valueUnit = math.unit(value, currentUnit);
        const convertedValue = math.number(valueUnit, targetUnit);

        if (!round) {
            return { value: convertedValue, unit: targetUnit };
        }

        // Round to the determined number of decimal places
        const roundedValue = roundToDecimalPlaces(convertedValue);

        return { value: roundedValue, unit: targetUnit };
    } catch (error) {
        // If the conversion throws, it means the two units cannot be converted to each other.
        // Return the value in the current unit
        console.error("error converting to unit", error);
        return { value: value, unit: currentUnit };
    }
};

/**
 * Check if a unit exists in the mathjs library
 */
function unitExists(unitString: string) {
    try {
        math.Unit.parse(unitString);
        return true;
    } catch (error) {
        return false;
    }
}

function canConvertUnits(currentUnit: string, targetUnit: string) {
    try {
        math.unit(1, currentUnit).to(targetUnit);
        return true;
    } catch (error) {
        return false;
    }
}
/**
 * Helper function to determine appropriate decimal places based on the number
 *
 * Shows more precision for small numbers and less precision for large numbers
 */
const getDecimalPlaces = (num: number): number => {
    const absNum = Math.abs(num);

    if (absNum < 0.001) return 6;

    if (absNum < 1) return 4;

    if (absNum < 100) return 2;

    if (absNum < 1000) return 1;

    // For very large numbers, show no decimal places
    return 0;
};

export const roundToDecimalPlaces = (num: number, decimalPlaces?: number) => {
    if (decimalPlaces === undefined) {
        decimalPlaces = getDecimalPlaces(num);
    }
    return Number(num.toFixed(decimalPlaces));
};
